<template>
  <DialogProvider>
    <div :class="{ 'adblock-enabled': isAdBlockEnabled }">
      <ToastContainer v-model:toasts="toastState.toasts" :max-toasts="MAX_TOASTS" @remove="toastContext.remove" />
      <div class="bootstrap bootstrap-scope">
        <ToastController ref="toastController" />
        <BModalOrchestrator teleport-to="#bootstrap-portals" />
        <BToastOrchestrator teleport-to="#bootstrap-portals" />
        <VsNotify group="alert" v-if="!isAdBlockEnabled">
          <template #body="props">
            <b-alert
              :class="props.class + ' mb-0'"
              :model-value="true"
              :variant="props.item.type"
              @close="props.close(props.item)"
              :dismissible="true"
            >
              {{ props.item.text }}
            </b-alert>
          </template>
        </VsNotify>
      </div>
      <div v-if="isError" class="text-center">
        <p>Failed to load the page</p>
        <BButton variant="primary" @click="reloadPage">Click to reload</BButton>
      </div>
      <component :is="layout" v-else-if="!isAdBlockEnabled">
        <router-view v-if="isInitialized && canViewRoute" :key="role" v-slot="{ Component }">
          <transition name="fade-transform" mode="out-in" @after-enter="afterEnter" @before-leave="beforeLeave">
            <component :is="Component" :key="transitionKey" />
          </transition>
        </router-view>
      </component>
      <div v-else class="adblock-enabled-message text-center">
        Please disable the ad blocker to use our platform.
        <button @click="reloadPage" class="btn btn-sm btn-primary mt-3">Refresh page</button>
      </div>
    </div>
  </DialogProvider>
</template>

<script setup>
import { createToastProvider, DialogProvider, provideToast, ToastContainer } from '@hprofits/ui';
import { BModalOrchestrator, BToastOrchestrator } from 'bootstrap-vue-next';
import { computed, getCurrentInstance, onBeforeMount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import ToastController from '@/components/utils/ToastController.vue';
import { useRoleWatcher } from '@/composables/useRoleWatcher';
import { useTheme } from '@/composables/useTheme';
import { useHead } from '@/hook/useHead';
import useScreenSetup from '@/hook/useScreenSetup';
import initializeApp from '@/initializeApp';
import BlacklistAutoRuleEditorLayout from '@/modules/blacklistAutoRules/components/Layout/BlacklistAutoRuleEditorLayout.vue';
import CampaignGroupLayoutRouterView from '@/modules/campaigns/components/Layout/CampaignGroupLayoutRouterView.vue';
import { setErrorToastHandler } from '@/plugins/vueQuery';
import { useEventBus } from '@/utils/bus';
import { checkGrantsPermission, checkPermission } from '@/utils/checkPermission.js';

import DefaultLayout from './layouts/default.vue';
import EmptyLayout from './layouts/empty.vue';
import checkAdBlock from './utils/checkAdBlock';

const store = useStore();
const route = useRoute();
const router = useRouter();
const instance = getCurrentInstance();

useTheme();
useHead();
useRoleWatcher();
useScreenSetup();

const { emit: busEmit } = useEventBus();
const MAX_TOASTS = 5;
const TOAST_DURATION_MS = 5000;

// Initialize toast system
const { state: toastState, context: toastContext } = createToastProvider(TOAST_DURATION_MS, MAX_TOASTS);
provideToast(toastContext);

// Connect toast system to Vue Query error handling
setErrorToastHandler((message) => toastContext.error(message));

const isAdBlockEnabled = ref(false);
const isInitialized = ref(false);
const isError = ref(false);

const layout = computed(() =>
  (() => {
    switch (route.meta.layout) {
      case 'blacklist-group':
        return 'div';
      case 'blacklist-editor':
        return BlacklistAutoRuleEditorLayout;
      case 'campaign':
        return CampaignGroupLayoutRouterView;
      case 'empty':
        return EmptyLayout;
      case 'default':
      default:
        return DefaultLayout;
    }
  })(),
);
const role = computed(() => store.getters.role);
// A role switch re-keys the page before the menu navigates away; without this gate a page the new
// role can't open is recreated for a moment and fires its requests under that role (#919).
const canViewRoute = computed(() => checkPermission(route.meta.roles, true) || checkGrantsPermission(route.meta));

const transitionKey = computed(() => {
  if (route.meta.transitionGroup) {
    return route.meta.transitionGroup;
  }
  return route.name;
});

const afterEnter = () => {
  const appContent = document.querySelector('.app-content');
  if (appContent) {
    appContent.classList.remove('wrapper');
  }
  busEmit('page-after-enter');
};

const beforeLeave = () => {
  const appContent = document.querySelector('.app-content');
  if (appContent) {
    appContent.classList.add('wrapper');
  }
};

const reloadPage = () => {
  window.location.reload();
};

onBeforeMount(async () => {
  isInitialized.value = false;
  try {
    await initializeApp({
      store,
      router,
      userSettings: instance.proxy.userSettings,
      config: instance.proxy.$config,
    });
  } catch {
    isError.value = true;
  } finally {
    isAdBlockEnabled.value = checkAdBlock.isDetected();
    isInitialized.value = !isError.value;
  }
});
</script>

<style lang="scss">
.bootstrap-scope {
  display: contents;
}

html,
body {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: 100%;
}
#app {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: 100%;

  &.adblock-enabled {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
.adblock-enabled-message {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
